import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EmbeddedViewRef,
  EnvironmentInjector,
  Injectable,
  Injector,
  Type
} from '@angular/core';
import {firstValueFrom, Subject} from 'rxjs';
import {take} from 'rxjs/operators';
import {PopoverWrapperComponent} from '../../shared-module-ui/components/popover-wrapper/popover-wrapper.component';

export interface PopoverOptions {
  component: Type<unknown>;
  backdropDismiss?: boolean;
  componentProps?: Record<string, unknown>;
  cssClass?: string | string[];
  event?: MouseEvent | PointerEvent | Event;
  showBackdrop?: boolean;
}

export interface PopoverDismissResult<DataType = unknown> {
  data: DataType;
  role: string | undefined;
}

export interface PopoverRef {
  dismiss<DataType>(data?: DataType, role?: string): Promise<boolean>;

  onDidDismiss<DataType>(): Promise<PopoverDismissResult<DataType>>;

  present(): Promise<void>;
}

@Injectable({
  providedIn: 'root'
})
export class PopoverControllerService {
  private activePopovers: PopoverInstance[] = [];

  constructor(
    private injector: Injector,
    private appRef: ApplicationRef
  ) {
  }

  async create(opts: PopoverOptions): Promise<PopoverRef> {
    const pop = new PopoverInstance(
      this.injector,
      this.appRef,
      {
        component: opts.component,
        componentProps: opts.componentProps || {},
        cssClass: opts.cssClass,
        showBackdrop: opts.showBackdrop !== false,
        backdropDismiss: opts.backdropDismiss !== false,
        event: opts.event
      },
      () => {
        this.removeInstance(pop);
      }
    );
    this.activePopovers.push(pop);
    return pop.getApi();
  }

  async dismiss<DataType>(data?: DataType, role?: string): Promise<boolean> {
    const top = this.getTop();
    if (top) {
      return top.dismiss(data, role);
    }
    return false;
  }

  getTop(): PopoverRef | undefined {
    const last = this.activePopovers[this.activePopovers.length - 1];
    return last ? last.getApi() : undefined;
  }

  private removeInstance(instance: PopoverInstance): void {
    const idx = this.activePopovers.indexOf(instance);
    if (idx > -1) {
      this.activePopovers.splice(idx, 1);
    }
  }
}

class PopoverInstance {
  private wrapperRef: ComponentRef<PopoverWrapperComponent>;
  private didDismiss = new Subject<PopoverDismissResult>();
  private lastRole: string | undefined;

  constructor(
    private injector: Injector,
    private appRef: ApplicationRef,
    private opts: PopoverOptions,
    private onRemove: () => void
  ) {
    this.wrapperRef = createComponent(PopoverWrapperComponent, {
      environmentInjector: this.injector as EnvironmentInjector
    });

    this.wrapperRef.instance.component = this.opts.component;
    this.wrapperRef.instance.componentProps = this.opts.componentProps;
    this.wrapperRef.instance.cssClass = this.opts.cssClass;
    this.wrapperRef.instance.showBackdrop = this.opts.showBackdrop;
    this.wrapperRef.instance.backdropDismiss = this.opts.backdropDismiss;
    this.wrapperRef.instance.event = this.opts.event;

    this.wrapperRef.instance.willDismiss$.pipe(take(1)).subscribe((payload) => {
      this.didDismiss.next({data: payload, role: this.lastRole});
      this.didDismiss.complete();
      this.onRemove();
      this.removeDomNode();
    });
  }

  async present(): Promise<void> {
    this.appRef.attachView(this.wrapperRef.hostView);
    const domElem = (this.wrapperRef.hostView as EmbeddedViewRef<unknown>).rootNodes[0] as HTMLElement;
    document.body.appendChild(domElem);
    this.wrapperRef.instance.present();
  }

  async dismiss<DataType>(data?: DataType, role?: string): Promise<boolean> {
    this.lastRole = role;
    this.wrapperRef.instance.dismiss(data);
    return true;
  }

  async onDidDismiss<DataType>(): Promise<PopoverDismissResult<DataType>> {
    return (await firstValueFrom(this.didDismiss)) as PopoverDismissResult<DataType>;
  }

  private removeDomNode(): void {
    this.appRef.detachView(this.wrapperRef.hostView);
    this.wrapperRef.destroy();
  }

  getApi(): PopoverRef {
    return {
      present: () => this.present(),
      dismiss: <DataType>(data?: DataType, role?: string) => this.dismiss(data, role),
      onDidDismiss: () => this.onDidDismiss()
    };
  }
}
